// A still picture of a zone on the street map, for printed route sheets: the
// map tiles drawn onto a canvas with the zone (and its route) on top.
// OpenStreetMap's tiles send CORS headers, so the canvas can be turned into an
// image and printed with the rest of the sheet. They only come at 256px, so
// on paper they're scaled up (smoothly) rather than swapped for sharper ones,
// which keeps the street names a readable size.
import { OUTLINE_COLOUR, ROUTE_COLOUR } from './leaflets';

const TILE = 256;
const tileUrl = (z, x, y) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;

// Web Mercator: [lat, lng] to world pixels at zoom z
export function project([lat, lng], z) {
  const size = TILE * 2 ** z;
  const sin = Math.sin((lat * Math.PI) / 180);
  return [((lng + 180) / 360) * size, (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * size];
}

function extent(points, z) {
  const px = points.map(p => project(p, z));
  const xs = px.map(p => p[0]);
  const ys = px.map(p => p[1]);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
}

// The closest zoom (17 at most, where house numbers start to show) that fits
// the points inside width x height, leaving `padding` around them.
export function fitZoom(points, width, height, padding) {
  for (let z = 17; z > 10; z--) {
    const e = extent(points, z);
    if (e.maxX - e.minX <= width - padding * 2 && e.maxY - e.minY <= height - padding * 2) return z;
  }
  return 10;
}

// A tile that won't load leaves a blank square rather than spoiling the sheet.
const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

function trace(ctx, points, toXY, close) {
  ctx.beginPath();
  points.forEach((p, i) => {
    const [x, y] = toXY(p);
    if (i) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
  });
  if (close) ctx.closePath();
}

function pin(ctx, [x, y], label, colour) {
  ctx.beginPath();
  ctx.arc(x, y, 10, 0, Math.PI * 2);
  ctx.fillStyle = colour;
  ctx.fill();
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = '#fff';
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.font = '700 11px "DM Sans", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, x, y + 0.5);
}

// Resolves to a JPEG data URL of width x height CSS pixels, drawn at `scale`
// times that for a sharp print.
export async function renderZoneMap({ polygon, route = [], width, height, scale = 2, padding = 36 }) {
  const points = [...polygon, ...route];
  const z = fitZoom(points, width, height, padding);
  const e = extent(points, z);
  const left = (e.minX + e.maxX) / 2 - width / 2;
  const top = (e.minY + e.maxY) / 2 - height / 2;
  const toXY = (p) => {
    const [x, y] = project(p, z);
    return [x - left, y - top];
  };

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  ctx.scale(scale, scale);
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#f2efe9';
  ctx.fillRect(0, 0, width, height);

  const count = 2 ** z;
  const draws = [];
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + width) / TILE); tx++) {
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + height) / TILE); ty++) {
      if (ty < 0 || ty >= count) continue;
      const x = tx;
      const y = ty;
      draws.push(loadImage(tileUrl(z, ((x % count) + count) % count, y)).then((img) => {
        if (img) ctx.drawImage(img, x * TILE - left, y * TILE - top, TILE, TILE);
      }));
    }
  }
  await Promise.all(draws);

  // Wash out everything outside the zone so the streets to do stand out
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, width, height);
  polygon.forEach((p, i) => {
    const [x, y] = toXY(p);
    if (i) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
  });
  ctx.closePath();
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.fill('evenodd');
  ctx.restore();

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  trace(ctx, polygon, toXY, true);
  ctx.lineWidth = 4;
  ctx.strokeStyle = OUTLINE_COLOUR;
  ctx.stroke();

  if (route.length > 1) {
    trace(ctx, route, toXY, false);
    ctx.setLineDash([9, 6]);
    ctx.lineWidth = 3;
    ctx.strokeStyle = ROUTE_COLOUR;
    ctx.stroke();
    ctx.setLineDash([]);
    pin(ctx, toXY(route[route.length - 1]), 'F', ROUTE_COLOUR);
    pin(ctx, toXY(route[0]), 'S', OUTLINE_COLOUR);
  }

  const credit = '\u00a9 OpenStreetMap contributors';
  ctx.font = '9px "DM Sans", Arial, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  const w = ctx.measureText(credit).width;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillRect(width - w - 10, height - 15, w + 10, 15);
  ctx.fillStyle = '#333';
  ctx.fillText(credit, width - 5, height - 4);

  return canvas.toDataURL('image/jpeg', 0.9);
}
